<template>
  <div class="autocomplete-popup__input-container q-input-group q-mb-1 q-input-group">
    <input
        v-model="searchQuery"
        @keyup="handleKeyUpConditional"
        @keyup.enter="handleRedirection"
        @focus="popupStore.showAutocomplete"
        type="text"
        class="autocomplete-search__input q-form-control q-position-relative q-rounded-3 q-shadow-sm"
        :class="{ 'q-input-with-button': showButtonOnInput && !isMobileView, 'autocomplete-search__input-max-width': autocomplete }"
        id="autocomplete-search__input"
        :placeholder="SmartSearchTranslations.autocomplete.whatSearch"
        :aria-label="SmartSearchTranslations.autocomplete.typeTerm"
        aria-describedby="search-input"
        autocomplete="off">
    <ClearQueryButton v-if="!autocomplete" :autocomplete="autocomplete"/>
    <button
        v-if="showButtonOnInput && ((!isMobileView && autocomplete) || !autocomplete)"
        class="autocomplete-search__input-button q-btn q-btn-danger q-shadow-sm"
        type="button"
        id="autocomplete-search__input-button"
        @click="handleRedirection">
      {{ SmartSearchTranslations.autocomplete.inputButton }}
    </button>
  </div>
</template>

<script setup lang="ts">
import {computed, defineProps, inject, ref, toRefs} from 'vue';
import {useSearchQueryStore} from "@/store/SearchQueryStore";
import {usePopupStore} from "@/store/AutocompletePopup/PopupStore";
import {useUrlStore} from "@/store/SerpPage/UrlStore";
import ClearQueryButton from "@/components/SerpPage/ResultsContainer/ClearQueryButton.vue";
import {SmartSearchConfigInterface, SmartSearchTranslationsInterface} from "@/core/types";
import {storeToRefs} from "pinia";
import {useMobileViewStore} from "@/store/MobileViewStore";
import DOMPurify from "dompurify";

const props = defineProps({
  autocomplete: Boolean
});

const urlStore = useUrlStore();
const queryStore = useSearchQueryStore();
const {searchQuery} = toRefs(queryStore);
const popupStore = usePopupStore();
const mobileViewStore = useMobileViewStore();
const {isMobileView} = storeToRefs(mobileViewStore);

const SmartSearchConfig = ref(inject<SmartSearchConfigInterface>('SmartSearchConfig')!);
const SmartSearchTranslations = ref(inject<SmartSearchTranslationsInterface>('SmartSearchTranslations')!);

const {redirectionUrl} = SmartSearchConfig.value.appConfiguration;
const showButtonOnInput = props.autocomplete ? SmartSearchConfig.value.autocomplete.showButtonOnInput : SmartSearchConfig.value.serp.showButtonOnInput;

const customRedirects = window?._QONizer?.customSmartSearchConfig?.customRedirects || [];

const matchedRedirect = computed(() => {
  const queryLower = searchQuery.value.toLowerCase().trim();
  return customRedirects.find(redirect => redirect.phrases.some(phrase => phrase.toLowerCase().trim() === queryLower));
});

const handleKeyUpConditional = () => {
  if (props.autocomplete) {
    popupStore.handleKeyUp();
  }
};

const handleRedirection = () => {
  if (matchedRedirect.value) {
    window.location.href = matchedRedirect.value.url;
  } else {
    const sanitised = DOMPurify.sanitize(queryStore.searchQuery);
    urlStore.goToSerpPage(`${redirectionUrl}?q=${sanitised}`, redirectionUrl);
  }
};
</script>

<style>
.autocomplete-search__input-max-width {
  max-width: 600px !important;
  margin-left: 0px;
}

.autocomplete-popup__input-container::before {
  content: "";
  display: block;
  position: absolute;
  background-color: rgb(149, 150, 153);
  -webkit-mask-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCI+PHRpdGxlPnNlYXJjaDwvdGl0bGU+PHJlY3Qgd2lkdGg9IjI0IiBoZWlnaHQ9IjI0IiBzdHlsZT0iZmlsbDojZmZmO29wYWNpdHk6MCIvPjxwYXRoIGQ9Ik0yMS44MSwyMC42NmwtNC41Mi00LjUxYTguNjMsOC42MywwLDEsMC0xLjA2LDEuMDZsNC41MSw0LjUxWk0zLjczLDEwLjczYTcsNywwLDEsMSw3LDdBNyw3LDAsMCwxLDMuNzMsMTAuNzNaIi8+PC9zdmc+");
  -webkit-mask-size: 20px 20px;
  -webkit-mask-repeat: no-repeat;
  width: 20px;
  height: 20px;
  top: 50%;
  left: 10px;
  transform: translate(0px, -50%);
  z-index: 1040;
}

.autocomplete-search__input {
  position: relative;
  padding-left: 35px !important;
  border: 1px solid #fff;
}

.autocomplete-search__input:focus {
  border: 1px solid var(--q-danger);
}

.q-input-with-button {
  border-bottom-right-radius: 0 !important;
  border-top-right-radius: 0 !important;
}

.q-input-with-button ~ .autocomplete-search__input-button {
}
</style>